import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAllPackages, assignInactivePackageToUser, assignPackageToUserWithExpiration, Package } from '../../services/packageService';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import './style.css';

interface PackageAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  userName: string;
  currentPackage?: string;
  currentPackageExpiresAt?: string | null;
  userRole: string;
}

const PackageAssignmentModal: React.FC<PackageAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userId,
  userName,
  currentPackage,
  currentPackageExpiresAt,
  userRole
}) => {
  const { language } = useLanguage();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [hasExpiration, setHasExpiration] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [durationDays, setDurationDays] = useState<number>(0);

  const t = (key: string) => {
    const translations: { [key: string]: { ua: string; en: string } } = {
      title: { ua: 'Призначення пакета', en: 'Assign Package' },
      subtitle: { ua: 'Оберіть пакет для користувача', en: 'Select a package for the user' },
      currentPackage: { ua: 'Поточний пакет', en: 'Current Package' },
      selectPackage: { ua: 'Оберіть пакет', en: 'Select Package' },
      noPackages: { ua: 'Немає доступних пакетів', en: 'No packages available' },
      expiration: { ua: 'Термін дії', en: 'Expiration' },
      expiresAt: { ua: 'Дата закінчення', en: 'Expiration Date' },
      durationDays: { ua: 'Тривалість (днів)', en: 'Duration (days)' },
      assign: { ua: 'Призначити', en: 'Assign' },
      cancel: { ua: 'Скасувати', en: 'Cancel' },
      assigning: { ua: 'Призначаємо...', en: 'Assigning...' },
      success: { ua: 'Пакет успішно призначено', en: 'Package assigned successfully' },
      error: { ua: 'Помилка призначення пакета', en: 'Error assigning package' }
    };
    return translations[key]?.[language as 'ua' | 'en'] || key;
  };



  useEffect(() => {
    if (isOpen) {
      loadPackages();
      setError(null);
      setSelectedPackageId('');
      setHasExpiration(false);
      setExpiresAt('');
      setDurationDays(0);
    }
  }, [isOpen, userRole]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const packagesData = await getAllPackages();
      // Filter only active packages
      let filteredPackages = packagesData.filter(pkg => pkg.is_active);

      // Filter by user role: owner -> owners, master -> masters, client -> clients
      let targetAudience = 'masters'; // default
      if (userRole === 'owner') {
        targetAudience = 'owners';
      } else if (userRole === 'client') {
        targetAudience = 'clients';
      } else if (userRole === 'master') {
        targetAudience = 'masters';
      }
      filteredPackages = filteredPackages.filter(pkg => pkg.target_audience === targetAudience);

      setPackages(filteredPackages);

      // Auto-select current package if it exists
      if (currentPackage) {
        const currentPkg = filteredPackages.find(pkg => pkg.name === currentPackage);
        if (currentPkg) {
          setSelectedPackageId(currentPkg.id);

          // Auto-populate expiration settings if current package has expiration
          if (currentPackageExpiresAt) {
            setHasExpiration(true);
            setExpiresAt(currentPackageExpiresAt);
            setDurationDays(0);
          }
        }
      }
    } catch (err) {
      console.error('Error loading packages:', err);
      setError(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedPackageId) return;

    try {
      setAssigning(true);
      setError(null);

      if (hasExpiration) {
        if (expiresAt) {
          await assignPackageToUserWithExpiration(userId, selectedPackageId, expiresAt);
        } else if (durationDays > 0) {
          await assignPackageToUserWithExpiration(userId, selectedPackageId, undefined, durationDays);
        } else {
          await assignInactivePackageToUser(userId, selectedPackageId);
        }
      } else {
        await assignInactivePackageToUser(userId, selectedPackageId);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error assigning package:', err);
      setError(err.message || t('error'));
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    if (!assigning) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="package-assignment-modal-backdrop">
      <div className="package-assignment-modal">
        <button className="modal-close-btn" onClick={handleClose} disabled={assigning}>
          ✕
        </button>

        <div className="modal-header">
          <div className="modal-icon">
            <i className="bi bi-box-seam"></i>
          </div>
          <h2>{t('title')}</h2>
          <p>
            {t('subtitle')} <strong>{userName}</strong>
          </p>
          {currentPackage && (
            <p className="current-package">
              {t('currentPackage')}: <strong>{currentPackage}</strong>
            </p>
          )}
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              <i className="bi bi-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Завантаження пакетів...</p>
            </div>
          ) : packages.length === 0 ? (
            <div className="no-packages">
              <i className="bi bi-info-circle"></i>
              <p>{t('noPackages')}</p>
            </div>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>{t('selectPackage')}</InputLabel>
              <Select
                value={selectedPackageId}
                label={t('selectPackage')}
                onChange={(e) => setSelectedPackageId(e.target.value)}
              >
                {packages.map((pkg) => (
                  <MenuItem key={pkg.id} value={pkg.id}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {pkg.name} <span style={{
                          border: `1px solid ${
                            pkg.target_audience === 'clients' ? '#28a745' :
                            pkg.target_audience === 'masters' ? '#007bff' :
                            pkg.target_audience === 'owners' ? '#8b5cf6' : '#6c757d'
                          }`,
                          padding: '1px 4px',
                          borderRadius: '3px',
                          fontSize: '0.75em'
                        }}>{pkg.target_audience}</span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        {pkg.description} - {pkg.price} {pkg.currency} ({language === 'ua' ? 'до' : 'up to'} {pkg.max_businesses} {language === 'ua' ? 'бізнесів' : 'businesses'})
                      </div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedPackageId && (
            <div className="expiration-settings">
              <label className="expiration-toggle">
                <input
                  type="checkbox"
                  checked={hasExpiration}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setHasExpiration(isChecked);
                    if (isChecked && !expiresAt && durationDays === 0) {
                      // When enabling expiration, default to date selection with 30 days from now
                      setExpiresAt(dayjs().add(30, 'day').endOf('day').toISOString());
                      setDurationDays(0);
                    }
                  }}
                />
                <span>{t('expiration')}</span>
              </label>

              {hasExpiration && (
                <div className="expiration-options">
                  <div className="option-group">
                    <label>
                      <input
                        type="radio"
                        name="expiration-type"
                        checked={expiresAt !== ''}
                        onChange={() => {
                          setExpiresAt(dayjs().add(30, 'day').endOf('day').toISOString());
                          setDurationDays(0);
                        }}
                      />
                      {t('expiresAt')}
                    </label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={t('expiresAt')}
                        value={expiresAt ? dayjs(expiresAt) : null}
                        onChange={(newValue: Dayjs | null) => {
                          if (newValue) {
                            setExpiresAt(newValue.endOf('day').toISOString());
                            setDurationDays(0);
                          } else {
                            setExpiresAt('');
                          }
                        }}
                        minDate={dayjs()}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>

                  <div className="option-group">
                    <label>
                      <input
                        type="radio"
                        name="expiration-type"
                        checked={durationDays > 0}
                        onChange={() => {
                          setDurationDays(30);
                          setExpiresAt('');
                        }}
                      />
                      {t('durationDays')}
                    </label>
                    <input
                      type="number"
                      value={durationDays}
                      onChange={(e) => {
                        setDurationDays(parseInt(e.target.value) || 0);
                        setExpiresAt('');
                      }}
                      min="1"
                      placeholder="30"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="cancel-btn"
            onClick={handleClose}
            disabled={assigning}
          >
            {t('cancel')}
          </button>
          <button
            className="assign-btn"
            onClick={handleAssign}
            disabled={!selectedPackageId || assigning}
          >
            {assigning ? (
              <>
                <span className="spinner"></span>
                {t('assigning')}
              </>
            ) : (
              t('assign')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageAssignmentModal;
